import { AsyncPipe, NgClass, TitleCasePipe } from '@angular/common';
import { Component, OnInit, ChangeDetectionStrategy, ElementRef, AfterViewInit, ChangeDetectorRef, OnDestroy, inject, viewChild } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { InputTextModule } from 'primeng/inputtext';
import { StyleClassModule } from 'primeng/styleclass';
import { ScrollPanelModule } from 'primeng/scrollpanel';
import { RouterModule, Router, NavigationStart } from '@angular/router';
import { Store } from '@ngrx/store';
import {
  COMPANY_INFO,
  STAFF_STATE,
  STATSIG_STATE,
  TENANTS_INFO,
} from '@fitness-force/state';
import {
  LangChangeEvent,
  TranslateModule,
  TranslateService,
} from '@ngx-translate/core';
import { ShortNamePipe } from '@fitness-force/pipes/short-name'
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TooltipModule } from 'primeng/tooltip';
import { AvatarModule } from 'primeng/avatar';
import { AvatarGroupModule } from 'primeng/avatargroup';
import { CONSTANTS_DATA } from '@fitness-force/constants';
import { FeatureFlagService,RouteStateService } from '@fitness-force/shared-utils';
// Import RouteStateService from shared-utils (best practice)
import { SidebarService } from '@fitness-force/shared';
import { BadgeModule } from 'primeng/badge';
import { StaffPreferencesComponent } from '../staff-preferences/staff-preferences.component';
import { AuthSevices } from '@fitness-force/api';

/* eslint-disable @angular-eslint/component-selector */
@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: true,
  imports: [
    StyleClassModule,
    SelectModule,
    InputTextModule,
    ScrollPanelModule,
    ButtonModule,
    RouterModule,
    TooltipModule,
    AvatarModule,
    AvatarGroupModule,
    ShortNamePipe,
    BadgeModule,
    StaffPreferencesComponent,
    TranslateModule,
    AsyncPipe,
    NgClass,
    TitleCasePipe
]
})
export class SidebarComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);
  private readonly cd = inject(ChangeDetectorRef);
  private readonly routeStateService = inject(RouteStateService);
  private readonly sidebarService = inject(SidebarService);
  private readonly featureFlagService = inject(FeatureFlagService);
  private readonly authService = inject(AuthSevices);

  readonly sidebar = viewChild<ElementRef>('sidebar');
  companyDetails: any;
  companyLogo!: Observable<any> | any;
  tenantLogo!: Observable<any> | any;
  selectedUrl: string;
  userDetails$ = new BehaviorSubject<any>([]);
  currentUserDetails: any;
  public companyInfoDetail$!: Observable<any>;
  public tenantInfoDetail$!: Observable<any>;

  sidebarMinimized: boolean = false;
  hoveMenu = false;
  activeIndex: number | null;
  Menus = new BehaviorSubject<Array<any>>([]);

  // Add destroy subject for subscription management
  private readonly destroy$ = new Subject<void>();

  AdminMenu = {
    title: 'ADMIN',
    url: '/admin',
    activeUrl: 'admin',
    icon: 'fa-cog',
    icon2: '',
    submenus: [],
  };
  CompanySettingsMenu = {
    title: 'COMPANY_SETTINGS',
    url: '/company-settings',
    activeUrl: 'company-settings',
    icon: 'fa-cog',
    icon2: '',
    submenus: [],
  };
  sidebarMinizeClass: string = 'lg:sticky';
  hideSidebar: boolean = false;
  langDirection = new BehaviorSubject<string>('ltr');
  showAvailablity = new BehaviorSubject<boolean>(false);
  showNotificationIcon = new BehaviorSubject<boolean>(false);
  showEnterpriseMaster = new BehaviorSubject<boolean>(false);
  showSidebar: boolean = false;
  selectedStaffId: number = 0;
  isPresaleTenant: boolean = false;
  constructor() {
    this.Menus.next(this.getInitialMenus());
    this.sidebarService.setMenus(this.Menus.value);

    this.router.events.subscribe({
      next: (event: any) => {
        if (event instanceof NavigationStart) {
          this.selectedUrl = event.url.split('?')[0];
        }
      },
    });
    this.CheckAndSetSelectedUrl();
  }

  async ngOnInit(): Promise<void> {
    this.companyInfoDetail$ = this.store.select(COMPANY_INFO);
    this.tenantInfoDetail$ = this.store.select(TENANTS_INFO);
    this.companyInfoDetail$.pipe(
      takeUntil(this.destroy$)
    ).subscribe((responseData) => {
      if (responseData?.selectedLang?.LANG_CODE) {
        this.translate.setDefaultLang(responseData?.selectedLang?.LANG_CODE);
        this.translate.use(responseData?.selectedLang?.LANG_CODE);
      }
      if (responseData.compDetails != null) {
        this.companyDetails = responseData.compDetails;
        this.companyLogo = this.companyDetails.logo;
      }


    });

    this.tenantInfoDetail$.pipe(
      takeUntil(this.destroy$)
    ).subscribe((responseData) => {
      if (responseData.selected_tenant) {
        this.tenantLogo = responseData.selected_tenant.image;
        if (responseData.selected_tenant.status.toLowerCase() == 'presales') {
          this.isPresaleTenant = false;
        }
      }
      this.currentUserDetails = this.getCurrentUserDetails();
      if (this.currentUserDetails) {
        this.store.select(STAFF_STATE).pipe(
          takeUntil(this.destroy$)
        ).subscribe(async (data: any) => {
          if (data?.staffList && data.staffList.length > 0) {
            const user = data.staffList.find((o: any) => { return o.id === parseInt(this.currentUserDetails.Id) });
            if (user) {
              this.userDetails$.next(user);
              this.selectedStaffId = user.id;
            }
          }
        });
      }
    });

    this.translate.onLangChange.subscribe((event: LangChangeEvent) => {
      const dir = CONSTANTS_DATA.ALL_LANGUAGES.find((lang) => (lang.LANG_CODE === event.lang))?.dir;
      if (dir)
        this.langDirection.next(dir);
    });


    this.cd.detectChanges();
  }

  private getCurrentUserDetails() {
    return this.authService.decodedToken ?? null;
  }

  private hasInitializedMenus = false;

  async ngAfterViewInit() {
    await this.timeout(1000);
    // feature-flag
    this.store.select(STATSIG_STATE).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (statsigUser: any) => {
        if (statsigUser?.user != null && !this.hasInitializedMenus) {
          this.hasInitializedMenus = true;
          this.updateMenusBasedOnFeatureFlags();
          this.sidebarService.updateMenusBasedOnStatsigState(statsigUser);
          this.Menus.next(this.sidebarService.getMenus());
          this.cd.detectChanges();
        }
      }
    });

    //this.updateMenusBasedOnFeatureFlags();

    this.featureFlagService.getFlag$('show_create_or_manage_staff_availability').pipe(
      takeUntil(this.destroy$)
    ).subscribe((showCreateOrManageStaffAvailability) => {
      this.showAvailablity.next(showCreateOrManageStaffAvailability);
    });

    this.featureFlagService.getFlag$('show_enterprise').pipe(
      takeUntil(this.destroy$)
    ).subscribe((showEnterprise) => {
      this.showEnterpriseMaster.next(showEnterprise);
    });
    this.cd.detectChanges();
  }

  updateMenusBasedOnFeatureFlags() {
    const menus = this.sidebarService.getMenus();
    this.sidebarService.updateMenuWithFlag(menus, "client-management", 'show_upgrade', {
      title: 'Upgrade',
      name: 'upgrade',
      url: '/client-management/upgrade',
      activeUrl: '/client-management/upgrade',
      icon: '',
      disabled: this.isPresaleTenant,
      order: 8,
    });

    this.sidebarService.updateMenuWithFlag(menus, "client-management", 'show_transfer', {
      title: 'Transfer',
      name: 'transfer',
      url: '/client-management/client-transfer',
      activeUrl: '/client-management/client-transfer',
      icon: '',
      disabled: this.isPresaleTenant,
      order: 9,
    });

    this.sidebarService.updateMenuWithFlag(menus, 'client-management', 'show_stop_billing', {
      order: 5,
      title: 'STOPPED_BILLING',
      name: 'stopped-billing',
      url: '/client-management/stopped-billing',
      activeUrl: '/client-management/stopped-billing',
      icon: 'fa-ban',
      disabled: false,
      dividerBefore: true,
    });

    this.sidebarService.updateMenuWithFlag(menus, 'client-management', 'show_subscription_status_override', {
      order: 6,
      title: 'SUBSCRIPTION_OVERRIDES',
      name: 'subscription-overrides',
      url: '/client-management/subscription-overrides',
      activeUrl: '/client-management/subscription-overrides',
      icon: 'fa-hand-paper',
      disabled: false,
    });

    this.sidebarService.updateMenuWithFlag(menus, 'client-management', 'show_subscription_amendment', {
      order: 13,
      title: 'SUBSCRIPTION_AMENDMENT',
      name: 'subscription-amendment',
      url: '/client-management/subscription-amendment',
      activeUrl: '/client-management/subscription-amendment',
      icon: 'fa-exchange-alt',
      disabled: false,
    });

    this.sidebarService.updateMenuWithFlag(menus, 'client-management', 'show_active_subscriptions_list', {
      order: 11,
      title: 'ACTIVE_SUBSCRIPTIONS',
      name: 'active-subscriptions',
      url: '/client-management/active-subscriptions',
      activeUrl: '/client-management/active-subscriptions',
      icon: 'fa-sync',
      disabled: false,
    });

    this.sidebarService.updateMenuWithFlag(menus, "pos", 'show_payengine_dispute_management',
      {
        title: 'DISPUTEMANAGEMENT',
        name: 'dispute-management',
        url: '/client-management/dispute-management',
        activeUrl: '/client-management/dispute-management',
        icon: 'fa-user-minus',
        disabled: false,
      });

    this.sidebarService.updateMenuWithFlag(menus, "pos", 'show_payengine_settlements',
      {
        title: 'SETTLEMENTS',
        name: 'settlements',
        url: '/client-management/settlements',
        activeUrl: '/client-management/settlements',
        icon: 'fa-user-minus',
        disabled: false,
      });

    this.sidebarService.updateMenuWithFlag(menus, "pos", 'show_payengine_transactions',
      {
        title: 'TRANSACTIONS',
        name: 'transactions',
        url: '/client-management/transactions',
        activeUrl: '/client-management/transactions',
        icon: 'fa-user-minus',
        disabled: false,
      });
    this.sidebarService.updateMenuWithFlag(menus, "pos", 'show_adhoc_charges',
      {
        title: 'ADHOC',
        name: 'adhoc-charges',
        url: '/pos/adhoc-charges',
        activeUrl: '/pos/adhoc-charges',
        icon: 'fa-chart-line',
        disabled: false,
      });
    this.sidebarService.updateMenuWithFlag(menus, "pos", 'show_corporate',
      {
        order: 2,
        title: 'CORPORATE_BILLING',
        name: 'corporate',
        url: '/pos/corporate',
        activeUrl: '/pos/corporate',
        icon: 'fa-chart-line',
        disabled: false,
        showPreview: true,
      });
    this.sidebarService.updateMenuWithFlag(menus, "pos", 'show_family_membership',
      {
        order: 3,
        title: 'FAMILY_MEMBERSHIPS',
        name: 'family-memberships',
        url: '/pos/family-memberships',
        activeUrl: '/pos/family-memberships',
        icon: 'fa-chart-line',
        disabled: false,
        showPreview: true,
      });
    this.sidebarService.updateMenuWithFlag(menus, "crm", 'show_staff_2way_message_notifciation',
      {
        title: 'INBOX',
        name: 'inbox',
        url: '/crm/inbox',
        activeUrl: '/crm/inbox',
        icon: '',
        disabled: false,
      });

    this.sidebarService.updateMenuWithFlag(menus, "/payout-engine", 'show_payoutengine_provider',
      {
        order: 7,
        title: 'PAYMENT_PROVIDERS',
        name: 'payment-providers',
        url: '/payout-engine/providers',
        activeUrl: '/payout-engine',
        icon: 'fa-credit-card',
        disabled: false
      });
    this.Menus.next(this.sidebarService.getMenus());
    this.cd.detectChanges();
  }

  CheckAndSetSelectedUrl(): void {
    const url = this.router.url;
    this.selectedMenu(url?.split('?')[0]);
  }

  toggleSideBarMinimized() {
    if (this.hoveMenu) this.hoveMenu = false;
    this.sidebarMinimized = !this.sidebarMinimized;
  }

  selectedMenu(url: string) {
    this.selectedUrl = url;
  }

  /**
   * Path-safe active check. Avoids `includes('report')` matching `/payout-engine/reports`.
   */
  isMenuActive(selectedUrl: string | undefined, activeUrl: string | undefined): boolean {
    if (!selectedUrl || !activeUrl) {
      return false;
    }

    const needle = activeUrl.startsWith('/') ? activeUrl : `/${activeUrl}`;
    if (selectedUrl === needle || selectedUrl.startsWith(`${needle}/`)) {
      return true;
    }

    // Bare tokens (e.g. "admin", "crm") must match a full path segment, not a substring.
    if (!activeUrl.includes('/')) {
      return selectedUrl.split('/').filter(Boolean).includes(activeUrl);
    }

    return false;
  }

  removeActiveIndex() {
    this.activeIndex == null;
  }

  client2LetterName(name: string): string {
    if (name) {
      const nameArray = name.split(" ");
      const newNameArray = [];
      for (let i = 0; i < nameArray.length; i++) {
        if (newNameArray.length >= 2)
          break;

        if (nameArray[i]) {
          newNameArray.push(nameArray[i]);
        }
      }
      return newNameArray.join(" ");
    }
    else {
      return "";
    }
  }

  getSortedSubmenus(submenus: any[]): any[] {
    if (!submenus || submenus.length === 0) {
      return [];
    }

    // Sort by order property if it exists, otherwise maintain original order
    return submenus.slice().sort((a, b) => {
      const orderA = a.order ?? Number.MAX_SAFE_INTEGER;
      const orderB = b.order ?? Number.MAX_SAFE_INTEGER;
      return orderA - orderB;
    });
  }

  getMenus() {
    return this.Menus.getValue()
  }

  timeout(ms: any) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  NavigateToStaffAvailability(staffId: number) {
    this.routeStateService.saveCurrentRoute();
    this.router.navigate(['/admin/staff-availability', staffId]);
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  handleCloseSidebar(_event: any) {
    this.showSidebar = false;
  }

  isUserCreatedWithinLast7Days(createdDate: string): boolean {
    const userCreatedDate = new Date(createdDate);
    const currentDate = new Date();
    const timeDifference = currentDate.getTime() - userCreatedDate.getTime();
    const daysDifference = timeDifference / (1000 * 3600 * 24);
    return daysDifference <= 7;
  }

  getInitialMenus(): any[] {
    return [
      {
        title: 'CRM',
        name: 'crm',
        url: '',
        activeUrl: 'crm',
        icon: 'fa-chart-pie',
        icon2: '',
        disabled: false,
        submenus: [
          {
            order: 1,
            title: 'PIPELINE',
            name: 'sales-pipeline',
            url: '/crm/sales-pipeline',
            activeUrl: '/crm/sales-pipeline',
            icon: '',
            disabled: false,
          },
          {
            order: 2,
            title: 'TASKS',
            name: 'tasks',
            url: '/crm/task-list',
            activeUrl: '/crm/task-list',
            icon: '',
            disabled: false,
          },
          {
            order: 3,
            title: 'PROSPECTS',
            name: 'prospects',
            url: '/crm/prospect-list',
            activeUrl: '/crm/prospect-list',
            icon: '',
            disabled: false,
          },
          {
            order: 4,
            title: 'SALES_CALENDER',
            name: 'sales-calendar',
            url: '/crm/sales-calendar',
            activeUrl: '/crm/sales-calendar',
            icon: '',
            disabled: false,
          },
          {
            order: 5,
            title: 'TRIALS',
            name: 'trials',
            url: '/crm/trial-list',
            activeUrl: '/crm/trial-list',
            icon: '',
            disabled: false,
          },
          {
            order: 6,
            title: 'CONTACTS',
            name: 'contacts',
            url: '/crm/contacts-list',
            activeUrl: '/crm/contacts-list',
            icon: '',
          }
        ],
      },
      {
        title: 'CLIENT_MANAGEMENT',
        name: 'client-management',
        url: '',
        activeUrl: 'client-management',
        icon: 'fa-users',
        icon2: '',
        disabled: false,
        submenus: [
          {
            order: 1,
            title: 'ACTIVE_CLIENTS',
            name: 'active-clients',
            url: '/client-management/active-clients',
            activeUrl: '/client-management/active-clients',
            icon: 'fa-user-plus',
            disabled: false,
          },
         
          {
            order: 3,
            title: 'EX_CLIENTS',
            name: 'ex-clients',
            url: '/client-management/ex-clients',
            activeUrl: '/client-management/ex-clients',
            icon: 'fa-user-minus',
            disabled: false,
          },
          {
            order: 4,
            title: 'IRREGULAR_CLIENTS',
            name: 'irregular-clients',
            url: '/client-management/irregular-clients',
            activeUrl: '/client-management/irregular-clients',
            icon: 'fa-user-minus',
            disabled: false,
          },
          {
            order: 7,
            title: 'CHECK_IN',
            name: 'check-in',
            url: '/client-management/check-in',
            activeUrl: 'client-management/check-in',
            icon: 'fa-user-minus',
            disabled: false,
            dividerBefore: true,
          },
          {
            order: 10,
            title: 'Freeze',
            name: 'freeze',
            url: '/client-management/freeze',
            activeUrl: '/client-management/freeze',
            icon: '',
            disabled: this.isPresaleTenant,
          },
          {
            order: 12,
            title: 'SUBSCRIPTION_CANCELLATION',
            name: 'subscription-cancellation',
            url: '/client-management/subscription-cancellation',
            activeUrl: '/client-management/subscription-cancellation',
            icon: 'fa-ban',
            disabled: false,
          }
        ],
      },
      {
        title: 'SCHEDULE',
        name: 'schedule',
        url: '',
        activeUrl: '/schedule',
        icon: 'fa-calendar-plus',
        icon2: '',
        disabled: false,
        submenus: [
          {
            order: 1,
            title: 'FITNESS_CALENDAR',
            name: 'fitness-calendar',
            url: '/schedule',
            activeUrl: '/schedule/fitness-calendar',
            icon: 'fa-calendar-plus',
            disabled: false,
          },
          {
            order: 2,
            title: 'SCHEDULE_CLASS',
            name: 'schedule-class',
            url: '/schedule/schedule-class',
            activeUrl: '/schedule/schedule-class',
            icon: 'fa-calendar-plus',
            disabled: false,
          },
          {
            order: 3,
            title: 'CLASS_LIST',
            name: 'class-list',
            url: '/schedule/class-list',
            activeUrl: '/schedule/class-list',
            icon: 'fa-calendar-plus',
            disabled: false,
          }
        ],
      },
      {
        title: 'POS',
        name: 'pos',
        url: '',
        activeUrl: 'pos',
        icon: 'fa-chart-line',
        icon2: '',
        disabled: false,
        submenus: [
          {
            order: 1,
            title: 'POS',
            name: 'pos-addon-charges',
            url: '/pos/pos-addon-charges',
            activeUrl: '/pos/pos-addon-charges',
            icon: 'fa-chart-line',
            disabled: false
          }
        ],
      },
      // {
      //   title: 'COMMISSION_MANAGEMENT',
      //   name: 'commission-management',
      //   url: '/commission-management/plans',
      //   activeUrl: 'commission-management',
      //   icon: 'fa-money-bill',
      //   icon2: '',
      //   disabled: false,
      //   submenus: [],
      // },
      {
        title: 'REPORT',
        name: 'report',
        url: '/report',
        activeUrl: '/report',
        icon: 'fa-chart-bar',
        icon2: '',
        disabled: false,
        submenus: [],
      }
    ];
  }

  openFeedback() {
    window.postMessage({
      target: 'FeaturebaseWidget',
      data: { action: 'openFeedbackWidget' },
    })
  }

  ngOnDestroy(): void {
    // Complete the destroy subject to unsubscribe from all observables
    this.destroy$.next();
    this.destroy$.complete();

    // Complete BehaviorSubjects
    this.Menus.complete();
    this.userDetails$.complete();
    this.langDirection.complete();
    this.showAvailablity.complete();
    this.showNotificationIcon.complete();
    this.showEnterpriseMaster.complete();
  }
}
